import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Editor } from "react-draft-wysiwyg";
import { DraftHandleValue, EditorState, RichUtils } from "draft-js";
import CustomImageDropdown, {
  CustomImageModalLocal,
} from "views/apps/paciente/ficha/historico/imageUpload/ImageUploadComponent";
import UploadFilesDropdown from "./UploadFilesDropdown";
import {
  ICropRequest,
  ImageBlockContext,
  imageBlockRenderFunc,
  updateImageData,
} from "./ImageBlock";
import ImageCropModal from "./ImageCropModal";

import { ConfirmAlert } from "components";

import { criarArquivo } from "helpers";
import { setLoadingState } from "redux/actions";
import { AppDispatch } from "redux/store";

import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

interface IHistoricoForm {
  editorState: EditorState;
  onEditorStateChange: (editorState: EditorState) => void;
  submit: () => Promise<void>;
  selectedFiles?: any[];
  setSelectedFiles?: React.Dispatch<React.SetStateAction<any[]>>;
  updating?: boolean;
  sendButton?: boolean;
  options?: string[];
  resetUpdate?: () => void;
  /** Mostra o "Descartar" também fora da edição (ex: rascunho de post novo). */
  discardable?: boolean;
  /** Se informado, pede confirmação com essa mensagem antes de descartar. */
  discardConfirmMessage?: string;
  placeholder?: string;
  localImageUpload?: boolean;
  onLocalFileSelected?: (blobUrl: string, file: File) => void;
  submitting?: boolean;
}

const CustomizedEditor: React.FC<IHistoricoForm> = ({
  editorState,
  submit,
  onEditorStateChange,
  selectedFiles,
  setSelectedFiles,
  updating,
  sendButton,
  options,
  resetUpdate,
  discardable,
  discardConfirmMessage,
  placeholder = "Descrição",
  localImageUpload,
  onLocalFileSelected,
  submitting,
}) => {
  const imageComponent = useMemo(() => {
    if (!localImageUpload) return CustomImageDropdown;

    const LocalImageComponent: React.FC<any> = (props) => (
      <CustomImageModalLocal {...props} onFileSelected={onLocalFileSelected} />
    );

    return LocalImageComponent;
  }, [localImageUpload, onLocalFileSelected]);

  const dispatch = useDispatch<AppDispatch>();
  const [cropRequest, setCropRequest] = useState<ICropRequest | null>(null);
  const showDiscard = !!resetUpdate && (updating || discardable);
  // O recorte é assíncrono: aplica sobre o estado mais recente do editor, não
  // sobre o capturado quando o modal abriu.
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;

  // Ref para o contexto ficar estável: o onEditorStateChange recebido costuma
  // ser recriado a cada render, o que re-renderizaria todas as imagens.
  const onEditorStateChangeRef = useRef(onEditorStateChange);
  onEditorStateChangeRef.current = onEditorStateChange;

  const imageBlockContext = useMemo(
    () => ({
      onCropRequest: setCropRequest,
      onChange: (state: EditorState) => onEditorStateChangeRef.current(state),
    }),
    []
  );

  const applyCrop = useCallback(
    async (file: File, widthFraction: number) => {
      if (!cropRequest) return;
      let src: string;

      if (localImageUpload) {
        // Mesmo fluxo de uma imagem recém-inserida: fica local (e no
        // rascunho) até o submit. A imagem anterior continua registrada para
        // que o desfazer (Ctrl+Z) ainda funcione.
        src = URL.createObjectURL(file);
        onLocalFileSelected?.(src, file);
      } else {
        dispatch(setLoadingState(true));
        try {
          const {
            data: {
              arquivos: [uploaded],
            },
          } = await criarArquivo([file], "1", "", true);
          src = uploaded.path;
        } finally {
          dispatch(setLoadingState(false));
        }
      }

      // Mantém a escala em que a imagem estava no editor: o pedaço recortado
      // ocupa o mesmo espaço que ocupava antes do recorte.
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = URL.createObjectURL(file);
      });
      URL.revokeObjectURL(img.src);
      const width = Math.max(
        60,
        Math.round(cropRequest.displayWidth * widthFraction) || img.naturalWidth
      );
      const height = Math.round((width * img.naturalHeight) / img.naturalWidth);

      onEditorStateChange(
        updateImageData(editorStateRef.current, cropRequest.entityKey, {
          src,
          width: `${width}px`,
          height: `${height}px`,
        })
      );
      setCropRequest(null);
    },
    [
      cropRequest,
      localImageUpload,
      onLocalFileSelected,
      onEditorStateChange,
      dispatch,
    ]
  );

  // O react-draft-wysiwyg só repassa ao RichUtils os comandos de estilo
  // inline; sem isso o backspace/delete padrão do draft-js não remove o bloco
  // atômico da imagem (o texto seguinte é "colado" nele e a imagem fica).
  const handleKeyCommand = (
    command: string,
    state: EditorState
  ): DraftHandleValue => {
    let nextState: EditorState | null = null;

    if (command === "backspace") nextState = RichUtils.onBackspace(state);
    else if (command === "delete") nextState = RichUtils.onDelete(state);
    else if (["bold", "italic", "underline"].includes(command))
      nextState = RichUtils.handleKeyCommand(state, command);

    if (!nextState) return "not-handled";
    onEditorStateChange(nextState);
    return "handled";
  };

  return (
    <ImageBlockContext.Provider value={imageBlockContext}>
      <Editor
        placeholder={placeholder}
        editorClassName="border-editor"
        toolbar={{
          options: options,
          inline: {
            inDropdown: false,
            options: ["bold", "italic", "underline"],
          },
          list: {
            inDropdown: false,
            options: ["unordered", "ordered"],
          },
          link: {
            inDropdown: false,
            showOpenOptionOnHover: true,
            defaultTargetOption: "_self",
            options: ["link"],
          },
          image: {
            component: imageComponent,
          },
        }}
        localization={{
          locale: "pt",
        }}
        onEditorStateChange={onEditorStateChange}
        editorState={editorState}
        handleKeyCommand={handleKeyCommand}
        customBlockRenderFunc={imageBlockRenderFunc as any}
        toolbarCustomButtons={
          selectedFiles && setSelectedFiles
            ? [
                <UploadFilesDropdown
                  selectedFiles={selectedFiles}
                  setSelectedFiles={setSelectedFiles}
                />,
              ]
            : []
        }
      />
      <div
        className={showDiscard ? "d-flex justify-content-between" : "text-end"}
      >
        {showDiscard && discardConfirmMessage ? (
          <ConfirmAlert
            className={false}
            text={discardConfirmMessage}
            confirmButtonText="Descartar"
            successTitle="Descartado"
            message_success={
              updating
                ? "As alterações foram descartadas."
                : "O rascunho foi descartado."
            }
            cb={() => resetUpdate?.()}
          >
            <button
              className="btn btn-secondary waves-effect waves-light mb-2"
              disabled={submitting}
            >
              Descartar
            </button>
          </ConfirmAlert>
        ) : showDiscard ? (
          <button
            className="btn btn-secondary waves-effect waves-light mb-2"
            onClick={resetUpdate}
            disabled={submitting}
          >
            Descartar
          </button>
        ) : null}
        {sendButton && (
          <button
            className="btn btn-primary waves-effect waves-light mb-2"
            onClick={submit}
            disabled={submitting}
          >
            <i
              className={
                submitting ? "mdi mdi-spin mdi-loading" : "mdi mdi-plus-circle"
              }
            ></i>{" "}
            {submitting ? "Enviando..." : updating ? "Atualizar" : "Enviar"}
          </button>
        )}
      </div>
      <ImageCropModal
        src={cropRequest?.src}
        onCancel={() => setCropRequest(null)}
        onConfirm={applyCrop}
      />
    </ImageBlockContext.Provider>
  );
};

export default CustomizedEditor;
